import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import en from './languages/en.json';
import fr from './languages/fr.json';

// Fonction utilitaire pour accéder aux textes du JSON de langue
function t(path, currentLang) {
  const lang = currentLang || (typeof window !== 'undefined' && window.localStorage?.getItem('lang')) || 'en';
  const translations = { en, fr };
  const dict = translations[lang] || translations['en'];
  return path.split('.').reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : path), dict);
}

function ChatInput({
  inputMessage,
  setInputMessage,
  isLoading,
  sendMessage,
  selectedFiles,
  setSelectedFiles,
  filePreviews,
  setFilePreviews,
  showRenameModal,
  lang: propLang
}) {
  const textareaRef = useRef(null);
  const fileInputRef = useRef();

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 200;
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    }
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFileIconClick = () => {
    if (!isLoading && selectedFiles.length < 3) fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const allowedTypes = ['image/png', 'image/jpeg'];
    const file = files[0];
    if (selectedFiles.length >= 3) return;
    if (!allowedTypes.includes(file.type)) return;
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSelectedFiles(prevFiles => {
          const arr = [...prevFiles, file].slice(0, 3);
          return arr;
        });
        setFilePreviews(prevPreviews => {
          const arr = [...prevPreviews, ev.target.result].slice(0, 3);
          return arr;
        });
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFiles(prevFiles => [...prevFiles, file].slice(0, 3));
      setFilePreviews(prevPreviews => [...prevPreviews, null].slice(0, 3));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (idx) => {
    setSelectedFiles(files => files.filter((_, i) => i !== idx));
    setFilePreviews(previews => previews.filter((_, i) => i !== idx));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // État pour le bouton Thinking activé/désactivé
  const [thinkingActive, setThinkingActive] = useState(false);
  const [showThinkingTooltip, setShowThinkingTooltip] = useState(false);
  const thinkingTooltipTimeout = useRef();

  return (
    <div className="chatgpt-input" style={showRenameModal ? {backdropFilter:'blur(12px)',WebkitBackdropFilter:'blur(12px)',transition:'backdrop-filter 0.28s'} : {}}>
      {/* Captcha invisible Turnstile */}
      {/* Captcha désactivé */}
      <div className="input-wrapper">
        <div className="unified-input-container">
          {/* Aperçu pièce jointe au-dessus de la barre de saisie */}
          {selectedFiles.length > 0 && (
            <div className="file-preview-modern file-preview-above file-preview-multi">
              {selectedFiles.map((file, idx) => (
                <div className="file-preview-square" key={idx} style={{display:'inline-block', marginRight:8}}>
                  {filePreviews[idx] ? (
                    <img src={filePreviews[idx]} alt="preview" className="file-preview-img" />
                  ) : (
                    <div className="file-preview-generic">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66L9.64 16.2a2 2 0 01-2.83-2.83l8.49-8.49" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                  <button onClick={() => handleRemoveFile(idx)} className="remove-file-btn-modern" title={t('chatInput.removeFile', propLang)}>✕</button>
                  <div className="file-preview-clip">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66L9.64 16.2a2 2 0 01-2.83-2.83l8.49-8.49" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="file-preview-filename">{file.name}</div>
                </div>
              ))}
            </div>
          )}
          {/* Barre de saisie */}
          <div className="search-bar file-preview-above-bar">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={t('chatInput.placeholder', propLang)}
              disabled={isLoading}
              rows={1}
              className="chatgpt-textarea"
            />
            <div className="search-buttons">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept="image/png,image/jpeg"
                disabled={isLoading || selectedFiles.length >= 3}
              />
              <button className="action-button" title={t('chatInput.attachFile', propLang)} onClick={handleFileIconClick} disabled={isLoading || selectedFiles.length >= 3}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66L9.64 16.2a2 2 0 01-2.83-2.83l8.49-8.49" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                onClick={sendMessage} 
                disabled={(!inputMessage.trim() && selectedFiles.length === 0) || isLoading}
                className="send-button-chatgpt"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          {/* Boutons outils en bas du même bloc */}
            <div className="bottom-tools">
              <div className="tools-left">
                <button
                  className="feature-button"
                  disabled
                  style={{opacity:0.5, cursor:'not-allowed', background:'#23242a', color:'#bbb', position:'relative', display:'inline-flex', width:'auto'}}
                  onMouseEnter={e => {
                    const tip = document.createElement('div');
                    tip.className = 'feature-tooltip';
                    
                    // Création sécurisée du contenu sans innerHTML
                    const mainDiv = document.createElement('div');
                    mainDiv.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:10px;';
                    
                    const span = document.createElement('span');
                    span.style.cssText = 'font-size:15px;white-space:nowrap;';
                    span.textContent = t('chatInput.decalSearchTooltip', propLang);
                    mainDiv.appendChild(span);
                    
                    const arrowDiv = document.createElement('div');
                    arrowDiv.style.cssText = 'width:100%;display:flex;justify-content:center;';
                    arrowDiv.innerHTML = `<svg width='18' height='10' style='display:block;margin-top:-2px;' viewBox='0 0 18 10'><polygon points='9,10 0,0 18,0' fill='#161616' stroke='#333333' stroke-width='1.5'/></svg>`;
                    
                    tip.appendChild(mainDiv);
                    tip.appendChild(arrowDiv);
                    tip.style.position = 'absolute';
                    tip.style.left = '0';
                    tip.style.top = '-44px';
                    tip.style.transform = 'none';
                    tip.style.marginLeft = '0px';
                    tip.style.background = 'rgba(22,22,22,0.65)';
                    tip.style.backdropFilter = 'blur(8px)';
                    tip.style.WebkitBackdropFilter = 'blur(8px)';
                    tip.style.border = '1.5px solid #333333';
                    tip.style.color = '#fff';
                    tip.style.padding = '8px 14px 2px 14px';
                    tip.style.borderRadius = '9px';
                    tip.style.fontSize = '15px';
                    tip.style.boxShadow = '0 2px 12px rgba(0,0,0,0.18)';
                    tip.style.zIndex = '10';
                    tip.style.whiteSpace = 'normal';
                    tip.style.textAlign = 'center';
                    tip.style.maxWidth = '180px';
                    tip.style.width = 'max-content';
                    tip.style.overflowWrap = 'break-word';
                    tip.style.display = 'block';
                    e.currentTarget.appendChild(tip);
                  }}
                  onMouseLeave={e => {
                    const tips = e.currentTarget.querySelectorAll('.feature-tooltip');
                    tips.forEach(tip => tip.remove());
                  }}
                >
                  <span className="button-icon">
                    {/* Icône loupe SVG */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" stroke="#bbb" strokeWidth="2" />
                      <line x1="17" y1="17" x2="22" y2="22" stroke="#bbb" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="button-text">{t('chatInput.decalSearch', propLang)}</span>
                </button>
                <div style={{position:'relative', display:'inline-block'}}>
                  <button
                    className="feature-button"
                    style={{
                      background: thinkingActive ? '#E36C48' : '#23242a',
                      color: thinkingActive ? '#fff' : '#bbb',
                      border: thinkingActive ? '1.5px solid #E36C48' : 'none',
                      boxShadow: thinkingActive ? '0 2px 8px rgba(227,108,72,0.12)' : 'none',
                      transition: 'background 0.2s, color 0.2s, border 0.2s',
                      position:'relative',
                      display:'inline-flex',
                      width:'auto'
                    }}
                    onClick={() => setThinkingActive(a => !a)}
                    onMouseEnter={() => {
                      thinkingTooltipTimeout.current = setTimeout(() => setShowThinkingTooltip(true), 1000);
                    }}
                    onMouseLeave={() => {
                      clearTimeout(thinkingTooltipTimeout.current);
                      setShowThinkingTooltip(false);
                    }}
                  >
                    <span className="button-icon">
                      {/* Icône ampoule SVG */}
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2a7 7 0 0 1 7 7c0 3.5-2.5 5.5-3.5 6.5V18a1 1 0 0 1-2 0v-2h-3v2a1 1 0 0 1-2 0v-2.5C7.5 14.5 5 12.5 5 9a7 7 0 0 1 7-7z" stroke={thinkingActive ? '#fff' : '#bbb'} strokeWidth="2" fill="none" />
                        <circle cx="12" cy="21" r="1" fill={thinkingActive ? '#fff' : '#bbb'} />
                      </svg>
                    </span>
                    <span className="button-text">{t('chatInput.thinking', propLang)}</span>
                  </button>
                  <AnimatePresence>
                    {showThinkingTooltip && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.7, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0, transition: { type: 'spring', bounce: 0.45, duration: 0.45 } }}
                        exit={{ opacity: 0, scale: 0.7, y: 20, transition: { duration: 0.18 } }}
                        style={{
                          position: 'absolute',
                          left: '50%',
                          bottom: 'calc(100% + 12px)',
                          transform: 'translateX(-50%)',
                          background: 'rgba(22,22,22,0.65)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          border: '1.5px solid #333333',
                          color: '#fff',
                          padding: '10px 16px 6px 16px',
                          borderRadius: '10px',
                          fontSize: '15px',
                          boxShadow: '0 4px 18px rgba(0,0,0,0.22)',
                          zIndex: 20,
                          whiteSpace: 'normal',
                          textAlign: 'center',
                          maxWidth: '260px',
                          width: 'max-content',
                          overflowWrap: 'break-word',
                          pointerEvents: 'none',
                          lineHeight: '1.7',
                        }}
                      >
                        {t('chatInput.thinkingTooltip', propLang)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <div className="tools-right">
                <button className="model-display" onClick={sendMessage} disabled={isLoading}>
                  <span className="model-name">{t('chatInput.modelName', propLang)}</span>
                </button>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}
export default ChatInput;
